import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MeetingService } from '../meeting.service';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProfileService } from '@/pages/profile/profile.service';
import { MessageService } from 'primeng/api';
import { MeetingWorkflowBlocksService } from '../meeting-workflow-blocks.service';
import { getEditMeetingFormObject } from './utils/edit-meeting-form-object';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { BehaviorSubject, of } from 'rxjs';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { MEETING_STATUS } from '@/types/meeting.types';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { newMeetingSchema } from '@/validations/meeting/new-meeting.validation';
import { User } from '@/types';
import { getAppMembersFormObject, getInvitationFormObject } from '../new-meeting/utils/add-guest-form-object';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { MeetingInviteeDialogComponent } from '@/components/shared/meeting-invitee-dialog/meeting-invitee-dialog.component';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { MeetingTransformationService } from '../meeting-transformation.service';

@Component({
  selector: 'sm-edit-meeting',
  imports: [PageHeaderComponent, FormBuilderComponent, StatusComponent, BlockBuilderComponent, MeetingInviteeDialogComponent],
  templateUrl: './edit-meeting.component.html',
  styleUrl: './edit-meeting.component.scss',
})
export class EditMeetingComponent implements OnInit, OnDestroy {
  meetingService = inject(MeetingService);
  meetingRepository = inject(MeetingRepository);
  meetingWorkflowBlocksService = inject(MeetingWorkflowBlocksService);
  comitteeService = inject(CommitteeService);
  translate = inject(TranslateService);
  router = inject(Router);
  profileService = inject(ProfileService);
  messageService = inject(MessageService);
  activatedRoute = inject(ActivatedRoute);
  meetingTransformationService = inject(MeetingTransformationService);
  breadcrumb = [
    {
      label: 'meeting.breadcrumb.meeting',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.breadcrumb.edit',
      routerLink: ['/meetings', 'edit'],
    },
  ];

  @ViewChild('dialog') dialog!: DialogComponent;

  loading = of(false);
  loadingComs$ = new BehaviorSubject<boolean>(false);
  isComDisabled$ = new BehaviorSubject<boolean>(true);
  loadingDraft$ = new BehaviorSubject<boolean>(false);
  loadingValidate$ = new BehaviorSubject<boolean>(false);
  loadingCancel$ = new BehaviorSubject<boolean>(false);
  loadingDelete$ = new BehaviorSubject<boolean>(false);
  disableDraft$ = new BehaviorSubject<boolean>(false);
  loadingSyncParticipants$ = new BehaviorSubject<boolean>(false);
  status: MEETING_STATUS = MEETING_STATUS.BROUILLON;

  controlBlocks: DynamicBlocks = this.meetingWorkflowBlocksService.getEditMeetingBlocks({
    store: this.meetingRepository,
    status: this.status,
    saveAsDraft: () => this.saveAsDraft(),
    loadingDraft: this.loadingDraft$,
    validateDraft: () => this.validateDraft(),
    loadingValidate: this.loadingValidate$,
    cancel: () => this.cancel(),
    loadingCancel: this.loadingCancel$,
    deleteMeeting: () => this.deleteMeeting(),
    loadingDelete: this.loadingDelete$,
  });

  comOptions: SelectOption[] = [];
  membersOptions: User[] = [];

  plannedDossiers: any[] = [];
  comMembers: User[] = [];

  formObject: DynamicForm = getEditMeetingFormObject({
    comOptions: [],
    store: this.meetingRepository,
    comMembers: [],
    plannedDossiers: this.plannedDossiers,
  });

  membersFormObject: DynamicForm = getAppMembersFormObject({
    membersOptions: this.membersOptions,
    store: this.meetingRepository,
    meetingService: this.meetingService,
  });

  inviteeFormObject: DynamicForm = getInvitationFormObject({
    store: this.meetingRepository,
  });

  id = this.activatedRoute.snapshot.params['id'];

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.meetingService.getOne(this.id).subscribe({
      next: response => {
        this.status = response.status;
        this.meetingTransformationService.setResponseToDtoOnLoadData(response);
        this.meetingRepository.set('serverState', this.meetingRepository.get('dto'));
        this.meetingRepository.set('comSelected', response.com ? { name: response.com.nom, code: response.com.id } : undefined);
        this.comMembers = response.participants;
        this.comOptions = response.com ? [{ name: response.com.nom, code: response.com.id }] : [];
        this.plannedDossiers = this.meetingTransformationService.mergePlannedDossiers();
        this.controlBlocks = this.meetingWorkflowBlocksService.getEditMeetingBlocks({
          store: this.meetingRepository,
          status: this.status,
          saveAsDraft: () => this.saveAsDraft(),
          loadingDraft: this.loadingDraft$,
          validateDraft: () => this.validateDraft(),
          loadingValidate: this.loadingValidate$,
          cancel: () => this.cancel(),
          loadingCancel: this.loadingCancel$,
          deleteMeeting: () => this.deleteMeeting(),
          loadingDelete: this.loadingDelete$,
          meetingDate: response.reunionDate,
          syncParticipants: () => this.syncParticipants(),
          loadingSyncParticipants: this.loadingSyncParticipants$,
          launchMeeting: () => this.launchMeeting(response.id),
        });
        const comId = this.meetingRepository.getNested('dto', 'comId');
        if (comId) {
          this.profileService.getUsersNotInCom(+comId).subscribe(users => {
            this.membersOptions = users;
            this.membersFormObject = getAppMembersFormObject({
              membersOptions: this.membersOptions,
              store: this.meetingRepository,
              meetingService: this.meetingService,
            });
          });
        }
        this.formObject = getEditMeetingFormObject({
          comOptions: this.comOptions,
          store: this.meetingRepository,
          createAction: () => this.dialog.toggleVisible(),
          status: this.status,
          plannedDossiers: this.plannedDossiers,
          comMembers: this.comMembers,
          isComDisabled: this.isComDisabled$,
        });
      },
      error: (error: any) => {
        this.loading = of(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.loading'),
        });
      },
    });
  }

  saveAsDraft() {
    const result = newMeetingSchema.safeParse(this.meetingRepository.get('dto'));
    this.checkPlannedDossiers();
    if (result.success) {
      this.loadingDraft$.next(true);
      this.meetingService.saveAsDraft(this.id, this.meetingRepository.get('dto')).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: this.translate.instant('meeting.message.success.saved_as_draft'),
          });
          this.loadingDraft$.next(false);
          this.loadData();
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.error.message || this.translate.instant('meeting.message.error.saved_as_draft'),
          });
          this.loadingDraft$.next(false);
        },
      });
    } else {
      this.meetingRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  validateDraft() {
    this.loadingValidate$.next(true);
    this.meetingService.validateDraft(this.id, this.meetingRepository.get('dto')).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.validated'),
        });
        this.loadingValidate$.next(false);
        window.location.reload();
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.validated'),
        });
        this.loadingValidate$.next(false);
      },
    });
  }

  cancel() {
    this.loadingCancel$.next(true);
    this.meetingService.cancel(this.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.cancelled'),
        });
        this.loadingCancel$.next(false);
        this.router.navigate(['/meetings']);
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.cancelled'),
        });
        this.loadingCancel$.next(false);
      },
    });
  }

  deleteMeeting() {
    this.loadingDelete$.next(true);
    this.meetingService.delete(this.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.deleted'),
        });
        this.loadingDelete$.next(false);
        this.router.navigate(['/meetings']);
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.deleted'),
        });
        this.loadingDelete$.next(false);
      },
    });
  }

  syncParticipants() {
    this.loadingSyncParticipants$.next(true);
    this.meetingService.syncParticipants(this.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.sync_participants'),
        });
        window.location.reload();
        this.loadingSyncParticipants$.next(false);
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.sync_participants'),
        });
        this.loadingSyncParticipants$.next(false);
      },
    });
  }

  launchMeeting(id: number) {
    this.meetingService.startMeeting(id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('meeting.message.success.launch'),
        });
        this.router.navigate(['/meetings', 'convene', id]);
      },
      error: (error: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message || this.translate.instant('meeting.message.error.launch'),
        });
      },
    });
  }

  private checkPlannedDossiers() {
    const reunionPpms = this.meetingRepository.getNested<any[]>('dto', 'reunionPpms');
    const reunionDacs = this.meetingRepository.getNested<any[]>('dto', 'reunionDacs');
    const reunionDemandes = this.meetingRepository.getNested<any[]>('dto', 'reunionDemandes');
    const reunionReports = this.meetingRepository.getNested<any[]>('dto', 'reunionReports');

    this.meetingRepository.get<any[]>('plannedDossiers').forEach(dossier => {
      if (dossier.type === 'PPM') {
        const found = reunionPpms.find(ppm => ppm.id === dossier.id);
        if (!found) {
          this.meetingRepository.setNested('dto', 'reunionPpms', [
            ...reunionPpms,
            {
              id: dossier.id,
              version: dossier.version,
              processInstanceId: dossier.processInstanceId,
              taskId: dossier.taskId,
              ref: dossier.ref,
              nom: dossier.nom,
              ac: dossier.ac,
            },
          ]);
        }
      } else if (dossier.type === 'DAC') {
        const found = reunionDacs.find(dac => dac.dacId === dossier.id);
        if (!found) {
          this.meetingRepository.setNested('dto', 'reunionsDacs', [
            ...reunionDacs,
            {
              dacId: dossier.dac.id,
              dacVer: dossier.dac.version,
              processInstanceId: dossier.processInstanceId,
              taskId: dossier.taskId,
            },
          ]);
        }
      } else if (dossier.type === 'ADDITIF') {
        const found = reunionDemandes.find(demande => demande.id === dossier.id);
        if (!found) {
          this.meetingRepository.setNested('dto', 'reunionDemandes', [
            ...reunionDemandes,
            {
              id: dossier.id,
              version: dossier.version,
              processInstanceId: dossier.processInstanceId,
              taskId: dossier.taskId,
            },
          ]);
        }
      } else if (dossier.type === 'REPORT') {

        const found = reunionReports.find(report => report.id === dossier.id);
        if (!found) {
          this.meetingRepository.setNested('dto', 'reunionReports', [
            ...reunionReports,
            {
              id: dossier.id,
              version: dossier.version,
              processInstanceId: dossier.processInstanceId,
              taskId: dossier.taskId,
            },
          ]);
        }
      }
    });
  }

  ngOnDestroy() {
    this.meetingRepository.reset();
  }
}
